/**
 * Tours and balloon flights are priced in WordPress from the operating dossier (RXRM_Dossier_Pricing).
 * The catalog sends each product's package, guide language and price per group size; this module turns
 * that into what the quote editor shows: the price for the group, what it includes, and its limits.
 * Internal costs and supplier payments from the internal quoting sheet stay out of customer quotes.
 */
export type DossierPackage = {
  package: string;
  language: "es" | "en";
  tiers: Record<string, number>;
  currency: string;
};

const couples = new Set(["couple", "couple_transfer", "couple_esencial"]);

/** What each package includes, written for the customer. */
const includes: Record<string, (guide: string) => string> = {
  shared: () =>
    "Vuelo compartido en globo con desayuno. Llegada por cuenta propia. No incluye transporte, guía ni entrada a la zona arqueológica.",
  shared_transfer: () =>
    "Vuelo compartido en globo, desayuno y traslado redondo desde zona céntrica de CDMX. No incluye guía ni entrada a la zona arqueológica.",
  shared_esencial: (guide) =>
    `Vuelo compartido en globo, desayuno, traslado redondo desde CDMX y recorrido Esencial con ${guide} (aprox. 1.5 h). No incluye entrada a la zona arqueológica.`,
  couple: () =>
    "Vuelo privado para dos personas (aprox. 45 min) con café, brindis, desayuno y certificado. Llegada por cuenta propia. No incluye transporte, visita guiada ni entrada a la zona arqueológica.",
  couple_transfer: () =>
    "Vuelo privado para dos personas (aprox. 45 min) con café, brindis, desayuno, certificado y traslado redondo desde CDMX. No incluye visita guiada ni entrada a la zona arqueológica.",
  couple_esencial: (guide) =>
    `Vuelo privado para dos personas (aprox. 45 min) con café, brindis, desayuno, certificado, traslado redondo desde CDMX y recorrido Esencial con ${guide} (aprox. 1.5 h). No incluye entrada a la zona arqueológica.`,
  guided: (guide) =>
    `Visita guiada con ${guide}. Llegada y regreso por cuenta propia. No incluye transporte, entrada a la zona arqueológica, alimentos ni vuelo.`,
  tour_esencial: (guide) =>
    `Tour con transporte y ${guide}. No incluye entrada a la zona arqueológica, alimentos ni vuelo.`,
  tour_full: (guide) =>
    `Tour con transporte y ${guide}. No incluye entrada a la zona arqueológica, alimentos ni vuelo.`,
};

export function dossierPackage(product: Record<string, unknown> | undefined): DossierPackage | null {
  const config = product?.dossier as Partial<DossierPackage> | null | undefined;
  if (!config?.package || !config.tiers || typeof config.tiers !== "object") return null;
  return {
    package: config.package,
    language: config.language === "en" ? "en" : "es",
    tiers: config.tiers,
    currency: config.currency ?? "MXN",
  };
}

/** Price for the group, or the reason this group size needs its own quote. */
export function packagePrice(config: DossierPackage, people: number):
  | { unit: number; total: number; perCouple: boolean }
  | { problem: string } {
  const sizes = Object.keys(config.tiers).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  const min = sizes[0] ?? 1;
  const max = sizes[sizes.length - 1] ?? min;
  if (couples.has(config.package) && people !== 2)
    return { problem: "Este paquete es privado para exactamente 2 personas." };
  const unit = config.tiers[String(people)];
  if (!Number.isInteger(people) || unit == null)
    return {
      problem:
        people > max
          ? `Para más de ${max} personas este paquete requiere cotización específica.`
          : `Este paquete es para ${min} a ${max} personas.`,
    };
  const perCouple = couples.has(config.package);
  return { unit, total: perCouple ? unit : unit * people, perCouple };
}

/** Customer-facing "what's included" text, used to start the quote's conditions. */
export function packageIncludes(config: DossierPackage): string {
  const guide = config.language === "en" ? "guía en inglés" : "guía en español";
  return includes[config.package]?.(guide) ?? "";
}
